<template>
    <div class="cart-total bg-gray-100">
        <div ref="cartTotalInner" class="cart-total-inner flex flex-col p-6">
            <div class="cart-total-details mt-2 text-gray-600 flex-grow">
                <!-- Subtotal -->
                <div v-if="total?.base_price" class="flex justify-between">
                    <span>{{ $translate('Subtotal') }}</span>
                    <span>{{ total?.base_price_formatted }}</span>
                </div>

                <!-- VAT details -->
                <div v-if="total?.vat_details">
                    <div v-for="vat in total.vat_details" :key="vat.vat_percentage" class="flex justify-between">
                        <span>{{ $translate('VAT Amount') }} ({{ vat.vat_percentage }}%)</span>
                        <span>{{ formatCurrency(vat.vat_amount) }}</span>
                    </div>
                </div>

                <!-- Total including VAT -->
                <div v-if="total?.total_price" class="flex justify-between">
                    <span>{{ $translate('Total including VAT') }}</span>
                    <span>{{ total?.total_price_formatted }}</span>
                </div>

                <!-- Discount -->
                <div v-if="total?.discount_price > 0" class="flex justify-between text-red-600">
                    <span>{{ $translate('Total Discount') }}</span>
                    <span>-{{ total?.discount_price_formatted }}</span>
                </div>

                <!-- Gift details section -->
                <div v-if="total?.gifts_details?.length" class="gift-details mt-4">
                    <div v-for="gift in total.gifts_details" :key="gift.id" class="flex justify-between">
                        <div class="gift-note">
                            <span>{{ $translate('Gift Code') }}: <strong class="text-green-700">{{ gift.code
                                    }}</strong></span>
                            <p class="text-xs text-gray-500">{{ gift.note }}</p>
                        </div>
                        <div class="gift-amount">
                            <strong class="text-red-700">-{{ gift.used_formatted }}</strong>
                            <span v-if="gift.remaining > 0" class="text-xs text-gray-500">
                                {{ $translate('Remaining Balance') }}: {{ gift.remaining_formatted }}
                            </span>
                        </div>
                    </div>
                </div>
                <!-- End of Gift details section -->

                <!-- Shipping Costs (if needed in the future) -->

                <div v-if="total?.shipping_cost" class="flex justify-between">
                    <span>{{ $translate('Shipping Costs') }}</span>
                    <span>{{ formatCurrency(total?.shipping_cost) }}</span>
                </div>

            </div>

            <!-- Total to Pay -->
            <div class="total text-xl font-bold mt-4 text-gray-800 flex justify-between flex-shrink-0">
                <h5>{{ $translate('Total to pay') }}</h5>
                <span>{{ total?.formatted }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue';
import { inject } from 'vue';

const cartStore = inject('cartStore');
const total = computed(() => cartStore.state.total);

const cartTotalInner = ref(null);

const scrollToBottom = () => {
    if (cartTotalInner.value) {
        cartTotalInner.value.scrollTop = cartTotalInner.value.scrollHeight;
    }
};

onMounted(() => {
    nextTick(() => {
        scrollToBottom();
    });
});

watch(total, () => {
    nextTick(() => {
        scrollToBottom();
    });
});

const formatCurrency = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('cs-CZ', {
        style: 'currency',
        currency: total.value?.currency?.code || 'CZK',
        minimumFractionDigits: 2
    }).format(value);
};
</script>

<style scoped>
.cart-total {
    border-top: 1px solid #e9ecef;
    border-bottom: solid 1px #444444;
    display: flex;
    flex-direction: column;
    height: 20vh;
    /* Can be adjusted as needed */
}

.cart-total-inner {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    flex-grow: 1;
}

.cart-total-details div {
    margin: 0.5rem 0;
}

.gift-details {
    margin-top: 1rem;
}

.gift-details .gift-note {
    display: flex;
    flex-direction: column;
}

.gift-details .gift-amount {
    display: flex;
    flex-direction: column;
    text-align: right;
}

.gift-details {
    border-top: dashed 3px #35d570;
    border-bottom: dashed 3px #35d570;
    background-color: #e8ffef;
}

.cart-total-inner .total {
    flex-shrink: 0;
}
</style>